import {
  call, put, takeEvery, select,
  actionChannel, take,
} from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';

import API from '../../api';
import { ChaptersActionTypes, ClaudeActionTypes } from '../actions/types';
import { ChaptersActionCreators, ClaudeActionCreators } from '../actions/creators';
import { AsyncActionStatus } from '../enums';
import { generateErrorMessage } from '../helpers';
import ChaptersSelectors from '../selectors/chapters.selectors';
import { CommentType } from '../../../../shared/enums';
import { toast } from '../../utilities';
import IncomingMessage from '../../../assets/audio/incoming_message.mp3';

const incomingMsg = new Audio(IncomingMessage);


function* addCommentToChapter(action) {
  try {
    const {
      chapterId,
      commentId,
      comment,
      chapterData,
    } = action.payload;

    const delta = chapterData?.titleDelta || chapterData?.descriptionDelta || chapterData?.delta;
    const response = yield call(
      API.Chapters.addCommentToChapter,
      chapterId,
      commentId,
      comment,
      delta,
    );
    yield put(ChaptersActionCreators.addCommentToChapter[AsyncActionStatus.FULFILLED](
      chapterId,
      response.data.comment,
    ));

    yield put(ChaptersActionCreators.updateChapter[AsyncActionStatus.PENDING](
      chapterId,
      chapterData,
    ));
  } catch (error) {
    yield put(ChaptersActionCreators.addCommentToChapter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* addReplyToCommentInChapter(action) {
  try {
    const {
      chapterId,
      commentId,
      reply,
    } = action.payload;
    const response = yield call(
      API.Chapters.addReplyToCommentInChapter,
      chapterId,
      commentId,
      reply._id,
      reply,
    );
    yield put(ChaptersActionCreators.addReplyToCommentInChapter[AsyncActionStatus.FULFILLED](
      chapterId,
      commentId,
      response.data.reply,
    ));
  } catch (error) {
    yield put(ChaptersActionCreators.addReplyToCommentInChapter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* updateReplyToCommentInChapter(action) {
  try {
    const response = yield call(
      API.Chapters.updateReplyToCommentInChapter,
      action.payload.chapterId,
      action.payload.commentId,
      action.payload.replyId,
      action.payload.replyText,
    );
    yield put(ChaptersActionCreators.updateReplyToCommentInChapter[AsyncActionStatus.FULFILLED](
      action.payload.chapterId,
      action.payload.commentId,
      response.data.reply,
    ));
  } catch (error) {
    yield put(ChaptersActionCreators.updateReplyToCommentInChapter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* updateChapter(action) {
  try {
    const response = yield call(
      API.Chapters.updateChapter,
      action.payload.chapterId,
      action.payload.chapter,
    );

    yield put(ChaptersActionCreators.updateChapter[AsyncActionStatus.FULFILLED](
      response.data.chapter,
    ));
    yield call(toast.success, t('toastMessages.success.contentUpdatedSuccessfully', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    yield put(ChaptersActionCreators.updateChapter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* updateCommentInChapter(action) {
  try {
    const response = yield call(
      API.Chapters.updateCommentInChapter,
      action.payload.chapterId,
      action.payload.commentId,
      action.payload.commentText,
    );
    yield put(ChaptersActionCreators.updateCommentInChapter[AsyncActionStatus.FULFILLED](
      action.payload.chapterId,
      response.data.comment,
    ));
  } catch (error) {
    yield put(ChaptersActionCreators.updateCommentInChapter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* resolveCommentInChapter(action) {
  try {
    const { chapterId, commentId, chapterData } = action.payload;

    const response = yield call(
      API.Chapters.resolveCommentInChapter,
      chapterId,
      commentId,
    );
    yield put(ChaptersActionCreators.resolveCommentInChapter[AsyncActionStatus.FULFILLED](
      chapterId,
      response.data.comment,
    ));
    yield put(ChaptersActionCreators.updateChapter[AsyncActionStatus.PENDING](
      chapterId,
      chapterData,
    ));
  } catch (error) {
    yield put(ChaptersActionCreators.resolveCommentInChapter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* deleteCommentInChapter(action) {
  try {
    const { chapterId, commentId, chapterData } = action.payload;

    const response = yield call(
      API.Chapters.deleteCommentInChapter,
      chapterId,
      commentId,
    );
    yield put(ChaptersActionCreators.deleteCommentInChapter[AsyncActionStatus.FULFILLED](
      chapterId,
      response.data.comment,
    ));

    yield put(ChaptersActionCreators.updateChapter[AsyncActionStatus.PENDING](
      chapterId,
      chapterData,
    ));
  } catch (error) {
    yield put(ChaptersActionCreators.deleteCommentInChapter[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* saveChaptersChanges(action) {
  try {
    const {
      chapters,
      chaptersToDelete,
      dedication,
      bookTitleIdeas,
      redirection,
      navigate,
      callback,
      bookId,
    } = action.payload;
    const response = yield call(API.Chapters.saveChaptersChanges, chapters, chaptersToDelete, dedication, bookTitleIdeas, bookId);
    yield put(ChaptersActionCreators.saveChaptersChanges[AsyncActionStatus.FULFILLED](
      response.data.chapters,
      response.data.book,
    ));
    if (redirection) yield call(navigate, redirection);
    if (callback) yield call(callback, response.data.chapters, response.data.book.bookSample);
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ChaptersActionCreators.saveChaptersChanges[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* watchDeleteOrUndoComments() {
  const requestChannel = yield actionChannel(ChaptersActionTypes.deleteOrUndoComments[AsyncActionStatus.PENDING]);

  while (true) {
    const { payload } = yield take(requestChannel);

    yield call(deleteOrUndoComments, payload);
  }
}

function* deleteOrUndoComments(payload) {
  try {
    const chapters = yield select(ChaptersSelectors.selectAllChapters);
    const { chapterId, deltaCommentsIds, chapterData } = payload;
    const chapterComments = chapters.find(chapter => chapter._id === chapterId)?.comments || [];

    let commentsToProcess = [];
    if (chapterData.delta) {
      commentsToProcess = chapterComments.filter(comment => comment.type === CommentType.DRAFT);
    } else if (chapterData.titleDelta) {
      commentsToProcess = chapterComments.filter(comment => comment.type === CommentType.CHAPTER_TITLE);
    } else if (chapterData.descriptionDelta) {
      commentsToProcess = chapterComments.filter(comment => comment.type === CommentType.CHAPTER_DESCRIPTION);
    }

    const commentsToDelete = commentsToProcess
      .reduce((comments, c) => {
        if (!deltaCommentsIds.includes(c._id) && !c.requestedToDelete && !c.resolved) comments.push(c._id);
        return comments;
      }, []);

    const commentsToUndo = commentsToProcess
      .reduce((comments, c) => {
        if (deltaCommentsIds.includes(c._id) && c.requestedToDelete) comments.push(c._id);
        return comments;
      }, []);

    if (isEmpty(commentsToDelete) && isEmpty(commentsToUndo)) {
      throw new Error('There are no comments to be processed');
    }

    let updatedComments = [];
    if (!isEmpty(commentsToDelete)) {
      const response = yield call(API.Chapters.deleteComments, chapterId, commentsToDelete);
      updatedComments = response.data.comments;
    } else if (!isEmpty(commentsToUndo)) {
      const response = yield call(API.Chapters.undoComments, chapterId, commentsToUndo);
      updatedComments = response.data.comments;
    }

    yield put(ChaptersActionCreators.deleteOrUndoComments[AsyncActionStatus.FULFILLED](chapterId, updatedComments));

    yield put(ChaptersActionCreators.updateChapter[AsyncActionStatus.PENDING](
      chapterId,
      chapterData,
    ));
  } catch (error) {
    yield put(ChaptersActionCreators.deleteOrUndoComments[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* fetchProofDataByUserId(action) {
  try {
    const response = yield call(
      API.Chapters.getProofDataByUserId,
      action.payload.userId,
    );
    yield put(ChaptersActionCreators.fetchProofDataByUserId[AsyncActionStatus.FULFILLED](
      response.data.chapters,
      response.data.project,
      response.data.book,
    ));
    if (action.payload.commentId
        && !response.data.book.pdfData.comments?.some((c) => c._id === action.payload.commentId && !c.resolved)) {
      yield call(toast.warning, t('toastMessages.warning.commentNoLongerExists', { ns: i18next.Namespace.COMMON }));
    }
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ChaptersActionCreators.fetchProofDataByUserId[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* fetchProofDataByProjectId(action) {
  try {
    const response = yield call(
      API.Chapters.getProofDataByProjectId,
      action.payload.projectId,
    );
    yield put(ChaptersActionCreators.fetchProofDataByProjectId[AsyncActionStatus.FULFILLED](
      response.data.chapters,
      response.data.project,
      response.data.book,
    ));
    if (action.payload.commentId
        && !response.data.book.pdfData.comments?.some((c) => c._id === action.payload.commentId && !c.resolved)) {
      yield call(toast.warning, t('toastMessages.warning.commentNoLongerExists', { ns: i18next.Namespace.COMMON }));
    }
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ChaptersActionCreators.fetchProofDataByProjectId[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* askClaude(action) {
  try {
    const {
      message, projectId, isTranscriptIncluded, tokensMultiplier,
    } = action.payload;

    const response = yield call(API.Chapters.askClaude, message, projectId, isTranscriptIncluded, tokensMultiplier);

    yield put(ClaudeActionCreators.askClaude[AsyncActionStatus.FULFILLED](
      response.data.answer,
    ));
    incomingMsg.play();
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ClaudeActionCreators.askClaude[AsyncActionStatus.REJECTED](
      message,
    ));
    incomingMsg.play();
    yield call(toast.error, message);
  }
}

function* wrapWithTagsAndSave(action) {
  try {
    const {
      message, projectId, callback,
    } = action.payload;
    const response = yield call(API.Chapters.wrapWithTagsAndSave, message, projectId);

    if (callback) yield call(callback);
    yield put(ClaudeActionCreators.wrapWithTagsAndSave[AsyncActionStatus.FULFILLED](
      response.data.answer,
      response.data.chapters,
    ));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ClaudeActionCreators.wrapWithTagsAndSave[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* generateClaudeReplyWithTranscript(action) {
  try {
    const { message, projectId, callback } = action.payload;
    const response = yield call(API.Chapters.generateClaudeReplyWithTranscript, message, projectId);

    if (callback) yield call(callback, response.data.text);
    yield put(ClaudeActionCreators.generateClaudeReplyWithTranscript[AsyncActionStatus.FULFILLED]());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ClaudeActionCreators.generateClaudeReplyWithTranscript[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

export default [
  watchDeleteOrUndoComments(),
  takeEvery(
    ChaptersActionTypes.addCommentToChapter[AsyncActionStatus.PENDING],
    addCommentToChapter,
  ),
  takeEvery(
    ChaptersActionTypes.addReplyToCommentInChapter[AsyncActionStatus.PENDING],
    addReplyToCommentInChapter,
  ),
  takeEvery(
    ChaptersActionTypes.updateChapter[AsyncActionStatus.PENDING],
    updateChapter,
  ),
  takeEvery(
    ChaptersActionTypes.updateCommentInChapter[AsyncActionStatus.PENDING],
    updateCommentInChapter,
  ),
  takeEvery(
    ChaptersActionTypes.updateReplyToCommentInChapter[AsyncActionStatus.PENDING],
    updateReplyToCommentInChapter,
  ),
  takeEvery(
    ChaptersActionTypes.resolveCommentInChapter[AsyncActionStatus.PENDING],
    resolveCommentInChapter,
  ),
  takeEvery(
    ChaptersActionTypes.deleteCommentInChapter[AsyncActionStatus.PENDING],
    deleteCommentInChapter,
  ),
  takeEvery(
    ChaptersActionTypes.saveChaptersChanges[AsyncActionStatus.PENDING],
    saveChaptersChanges,
  ),
  takeEvery(
    ChaptersActionTypes.fetchProofDataByUserId[AsyncActionStatus.PENDING],
    fetchProofDataByUserId,
  ),
  takeEvery(
    ChaptersActionTypes.fetchProofDataByProjectId[AsyncActionStatus.PENDING],
    fetchProofDataByProjectId,
  ),
  takeEvery(
    ClaudeActionTypes.askClaude[AsyncActionStatus.PENDING],
    askClaude,
  ),
  takeEvery(
    ClaudeActionTypes.wrapWithTagsAndSave[AsyncActionStatus.PENDING],
    wrapWithTagsAndSave,
  ),
  takeEvery(
    ClaudeActionTypes.generateClaudeReplyWithTranscript[AsyncActionStatus.PENDING],
    generateClaudeReplyWithTranscript,
  ),
];
